import {CopyToClipboardButton} from '@github-ui/copy-to-clipboard/Button'
import {
  readRepositoryFromFragment,
  readTopRepositoriesFromFragment,
  RepositoryPicker,
  type RepositoryPickerRepository,
} from '@github-ui/item-picker/RepositoryPicker'
import {ERRORS} from '@github-ui/item-picker-base/constants/errors'
import {DefaultBranchPickerAnchor} from '@github-ui/item-picker-branch/DefaultBranchPickerAnchor'
import {BranchPicker, type BranchPickerBranch} from '@github-ui/item-picker-branch-relay/BranchPicker'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {CopyIcon, GitBranchIcon, SyncIcon, TriangleDownIcon} from '@primer/octicons-react'
import {Button, FormControl, Radio, RadioGroup, TextInput} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {Suspense, useCallback, useEffect, useMemo, useState} from 'react'
import {graphql, useLazyLoadQuery, useRelayEnvironment} from 'react-relay'

import type {
  CreateLinkedBranchInput,
  createLinkedBranchMutation$data,
} from '../../../mutations/__generated__/createLinkedBranchMutation.graphql'
import {commitCreateLinkedBranchMutation} from '../../../mutations/create-linked-branch-mutation'
import {RepositoryPickerPlaceholder} from '../../RepositoryPlaceholder'
import type {CreateBranchDialogFindRepoBranchesQuery} from './__generated__/CreateBranchDialogFindRepoBranchesQuery.graphql'
import type {CreateBranchDialogReposQuery} from './__generated__/CreateBranchDialogReposQuery.graphql'
import styles from './CreateBranchDialog.module.css'
import {readRef} from './DevelopmentSection'
import {generateSuggestedBranchName} from './utils'

export type CreateBranchDialogProps = {
  issueId: string
  title: string
  number: number
  owner: string
  repo: string
  onClose: (nextStep: BranchNextStep, branchName: string | null) => void
}

export type CreateBranchDialogInternalProps = {
  issueId: string
  owner: string
  repo: string
  selectedBranchName: string
  onClose: (nextStep: BranchNextStep, branchName: string | null) => void
  setCreateBranchInput: (input: CreateLinkedBranchInput | null) => void
  setBranchSourceName: (name: string | null) => void
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const CreateBranchDialogFindRepoBranchesGraphqlQuery = graphql`
  query CreateBranchDialogFindRepoBranchesQuery($owner: String!, $repo: String!) {
    repository(owner: $owner, name: $repo) {
      id
      defaultBranchRef {
        ...DevelopmentSectionLinkedBranchRef
      }
      refs(refPrefix: "refs/heads/", first: 25) {
        edges {
          node {
            ...DevelopmentSectionLinkedBranchRef
          }
        }
      }
    }
  }
`

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const CreateBranchDialogReposGraphqlQuery = graphql`
  query CreateBranchDialogReposQuery($owner: String!, $repo: String!) {
    repository(owner: $owner, name: $repo) {
      # eslint-disable-next-line relay/must-colocate-fragment-spreads This will be fixed on the item picker is refactored
      ...RepositoryPickerRepository
    }
    viewer {
      # eslint-disable-next-line relay/must-colocate-fragment-spreads This will be fixed on the item picker is refactored
      ...RepositoryPickerTopRepositories
    }
  }
`

export type BranchNextStep = 'none' | 'local' | 'codespace' | 'desktop'

// Local BranchPickerPlaceholder component
const BranchPickerPlaceholder = ({currentBranch}: {currentBranch?: string | null}) => (
  <Button leadingVisual={GitBranchIcon} trailingVisual={TriangleDownIcon} disabled>
    {currentBranch ?? 'Select branch'}
  </Button>
)

export const CreateBranchDialog = ({title, number, owner, repo, onClose, ...rest}: CreateBranchDialogProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [createBranchInput, setCreateBranchInput] = useState<CreateLinkedBranchInput | null>(null)
  const {addToast} = useToastContext()
  const environment = useRelayEnvironment()
  const [branchSourceName, setBranchSourceName] = useState<string | null>(null)
  const suggestedBranchName = useMemo(() => generateSuggestedBranchName(number, title), [number, title])
  const [selectedBranchName, setSelectedBranchName] = useState<string>(suggestedBranchName)

  const [branchNextStep, setBranchNextStep] = useState<BranchNextStep>('local')
  const onChangeNextStep = useCallback((value: string | null) => {
    switch (value) {
      case 'codespace':
        setBranchNextStep('codespace')
        break
      case 'desktop':
        setBranchNextStep('desktop')
        break
      case 'local':
        setBranchNextStep('local')
        break
      default:
        setBranchNextStep('none')
    }
  }, [])

  const onCreateBranch = useCallback(() => {
    if (!createBranchInput) {
      return
    }
    setIsLoading(true)
    commitCreateLinkedBranchMutation({
      environment,
      input: createBranchInput,
      onError: e => {
        setIsLoading(false)
        const message = e?.name === 'LinkedBranchExists' ? e.message : ERRORS.couldNotCreateBranch
        // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
        addToast({
          type: 'error',
          message,
        })
      },
      onCompleted: (response: createLinkedBranchMutation$data) => {
        setIsLoading(false)
        // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
        addToast({
          type: 'success',
          message: 'Branch created',
        })
        onClose(branchNextStep, response.createLinkedBranch?.linkedBranch?.ref?.name ?? null)
      },
    })
  }, [addToast, branchNextStep, createBranchInput, environment, onClose])

  return (
    <Dialog
      width="large"
      height="auto"
      onClose={() => onClose('none', null)}
      title={'Create a branch for this issue'}
      renderFooter={() => (
        <>
          <Dialog.Footer className={styles.Dialog_Footer}>
            <RadioGroup name={'create-branch-next-action-group'} onChange={onChangeNextStep}>
              <RadioGroup.Label className={styles.RadioGroup_Label}>What&apos;s next?</RadioGroup.Label>
              <FormControl>
                <Radio name="create-branch-next-action" value="local" checked={branchNextStep === 'local'} />
                <FormControl.Label>Checkout locally</FormControl.Label>
              </FormControl>
              <FormControl>
                <Radio name="create-branch-next-action" value="desktop" />
                <FormControl.Label>Open branch with GitHub Desktop</FormControl.Label>
              </FormControl>
            </RadioGroup>
          </Dialog.Footer>
          <Dialog.Footer>
            <Button
              variant={'primary'}
              onClick={onCreateBranch}
              leadingVisual={isLoading ? SyncIcon : null}
              disabled={isLoading || !createBranchInput}
            >
              Create branch
            </Button>
          </Dialog.Footer>
        </>
      )}
    >
      <div data-testid={'branch-name'} className={styles.branchNameContainer}>
        <span className={styles.fieldLabel}>Branch name</span>
        <TextInput
          trailingAction={
            <CopyToClipboardButton
              textToCopy={selectedBranchName}
              ariaLabel={'Copy branch name to clipboard'}
              icon={CopyIcon}
              tooltipProps={{direction: 'w'}}
            />
          }
          value={selectedBranchName}
          onChange={e => setSelectedBranchName(e.target.value)}
          className={styles.TextInput}
        />
      </div>
      <Suspense fallback={<CreateBranchDialogInternalInputsLoading branchSourceName={branchSourceName} />}>
        <CreateBranchDialogInternal
          {...rest}
          owner={owner}
          repo={repo}
          onClose={onClose}
          selectedBranchName={selectedBranchName}
          setCreateBranchInput={setCreateBranchInput}
          setBranchSourceName={setBranchSourceName}
        />
      </Suspense>
    </Dialog>
  )
}

const CreateBranchDialogInternal = ({
  repo,
  owner,
  issueId,
  setCreateBranchInput,
  setBranchSourceName,
  selectedBranchName,
}: CreateBranchDialogInternalProps) => {
  const repos = useLazyLoadQuery<CreateBranchDialogReposQuery>(CreateBranchDialogReposGraphqlQuery, {
    repo,
    owner,
  })

  const topRepositories = readTopRepositoriesFromFragment(repos.viewer)

  const initialRepository = repos.repository ? readRepositoryFromFragment(repos.repository) : null
  const [selectedRepository, setSelectedRepository] = useState<RepositoryPickerRepository | null | undefined>(
    initialRepository,
  )

  const onSelectedRepository = useCallback((repository?: RepositoryPickerRepository) => {
    setSelectedRepository(repository)
  }, [])

  const onSelectRepository = useCallback(
    (selectedRepo?: RepositoryPickerRepository) => {
      if (selectedRepo && selectedRepo !== selectedRepository) {
        onSelectedRepository(selectedRepo)
      }
    },
    [onSelectedRepository, selectedRepository],
  )

  if (!selectedRepository) return null

  return (
    <CreateBranchDialogInternalInputs
      selectedRepository={selectedRepository}
      onSelectRepository={onSelectRepository}
      topRepositories={topRepositories}
      setCreateBranchInput={setCreateBranchInput}
      issueId={issueId}
      selectedBranchName={selectedBranchName}
      setBranchSourceName={setBranchSourceName}
    />
  )
}

type CreateBranchDialogInternalInputsProps = {
  selectedRepository: RepositoryPickerRepository
  onSelectRepository: (repo?: RepositoryPickerRepository) => void
  issueId: string
  topRepositories: RepositoryPickerRepository[]
  setCreateBranchInput: (input: CreateLinkedBranchInput | null) => void
  selectedBranchName: string
  setBranchSourceName: (name: string | null) => void
}

const CreateBranchDialogInternalInputs = ({
  selectedRepository,
  topRepositories,
  onSelectRepository,
  setCreateBranchInput,
  issueId,
  selectedBranchName,
  setBranchSourceName,
}: CreateBranchDialogInternalInputsProps) => {
  const data = useLazyLoadQuery<CreateBranchDialogFindRepoBranchesQuery>(
    CreateBranchDialogFindRepoBranchesGraphqlQuery,
    {
      repo: selectedRepository.name,
      owner: selectedRepository.owner.login,
    },
  )

  const repository = data.repository !== undefined ? data.repository : null
  const defaultBranchRef = repository?.defaultBranchRef ?? null
  const initialSourceBranch = readRef(defaultBranchRef)

  const [selectedSourceBranch, setSelectedSourceBranch] = useState<BranchPickerBranch | null | undefined>(
    initialSourceBranch,
  )

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSelectedSourceBranch(initialSourceBranch)
    setBranchSourceName(initialSourceBranch?.name ?? null)
    // we want to reset selected source branch and name when repository changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRepository])

  const onSelectedSourceBranch = useCallback(
    (selectedBranches?: BranchPickerBranch[]) => {
      const branch = selectedBranches && selectedBranches.length > 0 ? selectedBranches[0] : null
      setSelectedSourceBranch(current => branch ?? current)
      setBranchSourceName(initialSourceBranch?.name ?? null)
    },
    [initialSourceBranch?.name, setBranchSourceName],
  )

  const createBranchInput = useMemo(() => {
    if (!selectedRepository || !selectedSourceBranch || !selectedSourceBranch.target?.oid) {
      return null
    }
    return {
      repositoryId: selectedRepository.id,
      name: `refs/heads/${selectedBranchName}`,
      oid: selectedSourceBranch.target.oid,
      issueId,
    }
  }, [issueId, selectedBranchName, selectedRepository, selectedSourceBranch])

  useEffect(() => {
    setCreateBranchInput(createBranchInput)
  }, [createBranchInput, setCreateBranchInput])

  const branchesData = repository?.refs ?? null
  const branchPickerBranches = useMemo(() => {
    const repoBranches = branchesData?.edges
      ?.flatMap(edge => (edge?.node ? [readRef(edge.node)] : []))
      .filter((branch): branch is BranchPickerBranch => Boolean(branch))
    return repoBranches || []
  }, [branchesData])

  return (
    <div data-testid={'cb-container'} className={styles.repositoryBranchRow}>
      <div data-testid={'repository'} className={styles.repositoryPickerContainer}>
        <div data-testid={'repository-picker'} className={styles.repositoryPickerHeader}>
          <span className={styles.fieldLabel}>Repository destination</span>
        </div>
        <Suspense fallback={<RepositoryPickerPlaceholder />}>
          <RepositoryPicker
            initialRepository={selectedRepository}
            topRepositories={topRepositories}
            onSelect={onSelectRepository}
            options={{
              includeForks: true,
            }}
          />
        </Suspense>
      </div>
      <div data-testid={'source-branch-picker'} className={styles.branchSourceContainer}>
        <span className={styles.fieldLabel}>Branch source</span>
        <Suspense fallback={<BranchPickerPlaceholder />}>
          <BranchPicker
            activeBranch={selectedSourceBranch}
            anchorElement={anchorProps => (
              <DefaultBranchPickerAnchor {...anchorProps} branchName={selectedSourceBranch?.name} />
            )}
            defaultBranchId={initialSourceBranch?.id}
            branches={branchPickerBranches}
            owner={selectedRepository.owner.login}
            repo={selectedRepository.name}
            onSelectionChange={onSelectedSourceBranch}
            title={'Choose a source branch'}
          />
        </Suspense>
      </div>
    </div>
  )
}

type CreateBranchDialogInternalInputsLoadingProps = {
  branchSourceName: string | null
}

const CreateBranchDialogInternalInputsLoading = ({branchSourceName}: CreateBranchDialogInternalInputsLoadingProps) => {
  return (
    <div data-testid={'cb-container'} className={styles.loadingRepositoryBranchRow}>
      <div data-testid={'repository'} className={styles.branchNameContainer}>
        <div data-testid={'repository-picker'} className={styles.repositoryPickerHeader}>
          <span className={styles.fieldLabel}>Repository destination</span>
        </div>
        <RepositoryPickerPlaceholder />
      </div>
      <div data-testid={'source-branch-picker'} className={styles.loadingBranchSourceContainer}>
        <span className={styles.fieldLabel}>Branch source</span>
        <BranchPickerPlaceholder currentBranch={branchSourceName} />
      </div>
    </div>
  )
}
